import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Navigate } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";

import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";

import CircularProgress from "@mui/material/CircularProgress";
import LoginIcon from "@mui/icons-material/Login";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { apiSignIn } from "../../api/axios";
import { signIn } from "../../store/slice/authSlice";
import { encryptPassword } from "../../utils/crypto";
import Logo from "../../component/Logo";

import { companies } from "../../constants/companies";

const SignIn = () => {
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [errorMsg, setErrorMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const SignupSchema = Yup.object().shape({
    user: Yup.string().required("Obrigatório"),
    password: Yup.string().required("Obrigatório"),
    company: Yup.object({
      id: Yup.string().required(),
      label: Yup.string().required(),
    }),
  });

  const handleSubmit = async (form) => {
    try {
      const response = await apiSignIn(form);
      if (
        (response.status >= 300 && response.status <= 399) ||
        (response.status === 202 &&
          response.data &&
          typeof response.data === "string" &&
          response.data.includes("troca de senha"))
      ) {
        setErrorMsg(
          "Sua senha expirou. Defina uma nova senha acessando o sistema Protheus!"
        );
        return;
      }

      const userData = response.data;
      const encryptedPassword = await encryptPassword(form.password, userData.userName);

      dispatch(
        signIn({
          password: encryptedPassword,
          company: form.company,
          user: userData.userName,
          fullName: userData.userFullName,
          id: userData.userID,
          email: userData.email,
        })
      );

      navigate(`/`);
    } catch (error) {
      if (!error || !error.response) {
        setErrorMsg("Sem resposta do servidor!");
      } else if ([400, 401].includes(error.response.status)) {
        if (
          error &&
          error.response &&
          error.response.data &&
          error.response.data.errorMessage
        ) {
          setErrorMsg(error.response.data.errorMessage);
        } else {
          setErrorMsg("Verifique seu usuário e/ou senha!");
        }
      } else {
        setErrorMsg("Não foi possível realizar login!");
      }
    }
  };

  if (auth.isLoggedIn) {
    return <Navigate to="/" />;
  }

  return (
    <Box component="main" sx={{ bgcolor: "background.main" }}>
      <Container
        component="section"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={errorMsg.trim() !== ""}
          autoHideDuration={5000}
          onClose={() => setErrorMsg("")}
        >
          <Alert severity="error" onClose={() => setErrorMsg("")}>
            {`Ops! ${errorMsg}`}
          </Alert>
        </Snackbar>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Logo />
              </Box>

              <Typography
                component="h1"
                variant="h5"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                Solicitação de Serviço
              </Typography>

              <Formik
                validationSchema={SignupSchema}
                initialValues={{ user: "", password: "", company: "" }}
                onSubmit={handleSubmit}
                validateOnMount
              >
                {({
                  values,
                  errors,
                  touched,
                  isValid,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  setFieldValue,
                }) => (
                  <Box
                    component="form"
                    onSubmit={handleSubmit}
                    noValidate
                    sx={{ mt: 1 }}
                  >
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="user"
                      label="Usuário"
                      name="user"
                      autoFocus
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.user}
                      error={touched.user && Boolean(errors.user)}
                      helperText={touched.user && errors.user}
                    />

                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="password"
                      label="Senha"
                      type={showPassword ? "text" : "password"}
                      id="password"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.password}
                      autoComplete="off"
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPassword ? "ocultar senha" : "mostrar senha"
                              }
                              onClick={() => setShowPassword((show) => !show)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Autocomplete
                      sx={{ marginTop: 1.5 }}
                      noOptionsText="Não localizado"
                      fullWidth
                      id="company"
                      name="company"
                      options={companies}
                      onChange={(e, newValue) => {
                        setFieldValue(
                          "company",
                          newValue !== null ? newValue : values.company
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          label="Empresa/Filial"
                          fullWidth
                          name="company"
                          {...params}
                        />
                      )}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{ mt: 3, mb: 2 }}
                      disabled={!isValid || isSubmitting}
                      endIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <LoginIcon />}
                    >
                      {isSubmitting ? "Entrando..." : "Entrar"}
                    </Button>
                  </Box>
                )}
              </Formik>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default SignIn;
